// ─────────────────────────────────────────────
//  internalSchedule — pure rules for the Schedule module's Close/Reopen flow.
//
//  Closing a schedule finalizes it: rows with no data are DELETED (they never
//  become OR cards), and only complete rows transfer to the day's assignments.
//  Definitions:
//    • EMPTY row  — carries nothing at all (no surgeon/procedure/times/staff).
//    • COMPLETE room row — has a room, a start time, and at least one person
//      (CRNA/AA or attending). Solo-MD and no-surgeon cases still count.
//    • BENCH row — no room but a CRNA (on-call / float): transfers as the
//      unassigned bench, never as a card.
//  Anything in between (partial rows) is kept in the schedule for the admin
//  to finish, but does NOT transfer.
// ─────────────────────────────────────────────

const filled = (v) => String(v ?? "").trim() !== "";

export function isEmptyScheduleRow(r) {
  if (!r) return true;
  return !(
    filled(r.surgeon) || filled(r.procedure) || filled(r.specialty) ||
    filled(r.startTime) || filled(r.endTime) ||
    r.attendingId || r.ahpId || filled(r.ahpShiftCode)
  );
}

export function isCompleteScheduleRow(r) {
  if (!r) return false;
  return filled(r.orRoom) && filled(r.startTime) && Boolean(r.ahpId || r.attendingId);
}

export function isBenchScheduleRow(r) {
  return !!r && !filled(r.orRoom) && Boolean(r.ahpId);
}

// Partition a date's schedule rows for Close:
//   apply     — complete room rows → assignments (via linkProviderChains +
//               buildBoardWrites)
//   benches   — bench rows → the unassigned pool
//   deletions — EMPTY rows to remove (never become cards)
//   kept      — partial rows left in the schedule untouched
export function splitForClose(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const apply = [];
  const benches = [];
  const deletions = [];
  const kept = [];
  for (const r of list) {
    if (isCompleteScheduleRow(r)) apply.push(r);
    else if (isBenchScheduleRow(r)) benches.push(r);
    else if (isEmptyScheduleRow(r)) deletions.push(r);
    else kept.push(r);
  }
  return { apply, benches, deletions, kept };
}
